import React, {useState} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {useAsync} from 'react-use'
import {MBMemberError} from '../../../Common/MBMemberError'
import {Spacer} from '../../../../Common/Components/Spacer'
import {MemberMailUpdateDTO} from '../../../DTO/MemberMailUpdateDTO'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {CommonResponseDTO, CommonResponseStatus} from '../../../../Network/CommonResponse'
import {MBMemberEMailInput} from '../../Atoms/MBMemberEMailInput'
import {validateMemberMailInput, ValidationMailInput} from '../../../Utils/Validations/ValidateMembersMailInput'
import {Nullable} from '../../../../Common/TypeHelper'
import {animateScroll} from 'react-scroll'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {ContentBlock} from '../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../Molecules/BaseContentBlocks/ContentTitle'
import {ContentTopMessage} from '../../Molecules/BaseContentBlocks/ContentTopMessage'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {ContentNotes} from '../../Molecules/BaseContentBlocks/ContentNotes'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {MBContentBodyTypography, MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import styled from '@emotion/styled'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {memberResponseStatusCheck} from '../../../Utils/MemberResponseStatusCheck'
import {useTranslation} from 'react-i18next'
import {MemberCircleArrowDownIcon} from '../../Atoms/Icons/MemberCircleArrowDownIcon'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

type State = {
  mail: string
}

type ErrorState = {
  isInputError: boolean
  mailErrors: string[]
}

export function MembersEditMailInputPage(props: Props): Nullable<JSX.Element> {
  const {t} = useTranslation('member')
  const [state, setState] = useState<State>({mail: ''})
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [processing, setProcessing] = useState<boolean>(false)
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    mailErrors: [],
  })

  const throwMBMemberError = useMBMemberError()

  const result = useAsync(async () => {
    return await props.memberRepo.getSelf()
  })

  if (result.loading) {
    return null
  }

  if (result.error) {
    throw new MBMemberError(result.error.message)
  }

  if (!result.value) {
    throw new MBMemberError('UnknownError')
  }

  if (!result.value.data.member) {
    throw new MBMemberError('UnknownError')
  }

  memberResponseStatusCheck(result.value.status)

  function checkStatus(response: CommonResponseDTO<null>): void {
    switch (response.status) {
      case CommonResponseStatus.Ok:
        props.navigator.goToEditMailInvitationCompletePage()
        break
      case CommonResponseStatus.BadRequest:
        if (response.errorMessageList !== null && response.errorMessageList.length !== 0) {
          setProcessing(false)
          setErrorMessages(response.errorMessageList)
        }
        break
      case CommonResponseStatus.InternalServerError:
        throw new MBMemberError('ServerError')
      default:
        throw new MBMemberError('UnknownError')
    }
  }

  async function goToEditMailInvitationCompletePage(): Promise<void> {
    setProcessing(true)

    const inputForValidation: ValidationMailInput = {
      ...state,
      mail: state.mail.trim(),
    }
    setState((prevState) => ({
      ...prevState,
      ...inputForValidation,
    }))

    const validationResult = validateMemberMailInput(
      inputForValidation,
    )

    if (validationResult.hasError) {
      setProcessing(false)
      setErrorState({
        isInputError: true,
        mailErrors: validationResult.errors.mail,
      })
      animateScroll.scrollToTop()
      return
    }

    try {
      const response =
        await props.memberRepo.postOrderForUpdateMail(
          new MemberMailUpdateDTO(inputForValidation.mail),
        )

      checkStatus(response)

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }

  function goToAccountInformationPage(): void {
    props.navigator.goToAccountInformationPage()
  }

  function handleChangeInput(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const {name, value} = e.target
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const {mail} = result.value.data.member
  const {isInputError, mailErrors} = errorState

  return (
    <MembersPageTemplate>
      <PageTitle>メールアドレス変更入力</PageTitle>

      <InputErrorSummary display={isInputError}/>

      <ContentBlock data-testid={'block-mail'}>
        <ContentTitle>メールアドレス入力</ContentTitle>
        <ContentTopMessage>
          新しいメールアドレスを入力してください。<br/>
          ご入力いただいたメールアドレス宛にメールアドレス変更のためのURLをお送りいたします。
        </ContentTopMessage>

        <Spacer mt={'18px'}>
          <MBContentBodyDivider/>
        </Spacer>

        <Spacer mt={'18px'} mb={'18px'}>
          <MBContentSubTitleTypography>
            {t('メールアドレス変更入力.メールアドレス入力.ラベル 現在のメールアドレス')}
          </MBContentSubTitleTypography>
          <Spacer mt={'10px'} ml={'12px'}>
            <MBContentBodyTypography>{mail}</MBContentBodyTypography>
          </Spacer>

          <ArrowDownIconArea>
            <MemberCircleArrowDownIcon/>
          </ArrowDownIconArea>

          <label htmlFor="email">
            <MBContentSubTitleTypography>
              {t('メールアドレス変更入力.メールアドレス入力.ラベル 新しいメールアドレス')}
            </MBContentSubTitleTypography>
          </label>
          <Spacer mt={'10px'}>
            <MBMemberEMailInput
              name="mail"
              value={state.mail}
              id="email"
              hasError={mailErrors.length > 0}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => handleChangeInput(e)}
            />
            <ErrorDisplay errors={mailErrors}/>
          </Spacer>
        </Spacer>

        <MBContentBodyDivider/>

        <Spacer mt={'14px'}>
          <ContentNotes
            notes={[t('メールアドレス変更入力.メールアドレス入力.注記 メール受信設定')]}
          />
        </Spacer>

      </ContentBlock>

      <MessageForAction>
        上記の内容に間違いがなければ<br/>
        『送信する』ボタンを押してください。
      </MessageForAction>

      <ErrorDisplay errors={errorMessages}/>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'send-button'}
          disabled={processing}
          onClick={(): void => {
            goToEditMailInvitationCompletePage()
          }}
        >
          送信する
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={goToAccountInformationPage}>
          登録情報に戻る
        </PIMSecondaryButton>
      </PIMPageActions>
    </MembersPageTemplate>
  )
}

const ArrowDownIconArea = styled.div`
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
`
